import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Building, Users, Award, ArrowRight, MapPin, Star, Heart } from 'lucide-react';
import { useScrollAnimation, createScrollAnimationClasses } from '../hooks/useScrollAnimation';

export const AboutSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ delay: 0 });
  const [contentRef, contentVisible] = useScrollAnimation({ delay: 200 });
  const [imagesRef, imagesVisible] = useScrollAnimation({ delay: 400 });
  const [statsRef, statsVisible] = useScrollAnimation({ delay: 600 });
  const [cardsRef, cardsVisible] = useScrollAnimation({ delay: 800 });

  return (
    <section id="about" className="py-24 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-80 h-80 bg-blue-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-green-200/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-purple-200/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div ref={headerRef} className={`text-center mb-20 ${createScrollAnimationClasses('fadeInUp', headerVisible)}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-6 py-3 rounded-full mb-6 border border-blue-200/50">
            <MapPin size={16} className="text-blue-600" />
            <Badge className="bg-transparent text-blue-700 px-0 py-0 text-sm font-semibold">
              About TSI
            </Badge>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Transforming Oman's
            <span 
              className="block bg-clip-text text-transparent transition-all duration-300 hover:scale-105"
              style={{ 
                backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))' 
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundImage = 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundImage = 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))';
              }}
            >
              Urban Infrastructure
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            As the leading provider of smart traffic technology in the Sultanate of Oman, 
            we combine cutting-edge innovation with deep understanding of local infrastructure needs, 
            creating sustainable solutions that honor tradition while embracing the future.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Content - Slides from Left */}
          <div ref={contentRef} className={createScrollAnimationClasses('fadeInLeft', contentVisible)}>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-100 to-emerald-100 px-4 py-2 rounded-full mb-6">
              <Star size={14} className="text-green-600" />
              <span className="text-green-700 text-sm font-semibold">Our Story</span>
            </div>
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              Pioneering Smart Cities Across Oman
            </h3>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              From the bustling streets of Muscat to the growing cities of Salalah and Sohar, 
              Traffic Service International has been at the forefront of transforming Oman's 
              urban landscape with intelligent transportation solutions.
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Our commitment to innovation extends beyond technology – we invest $1 million 
              annually in research and development right here in Oman, ensuring our solutions 
              are perfectly adapted to the unique challenges and opportunities of the region.
            </p>

            {/* Key Stats */}
            <div ref={statsRef} className={`grid grid-cols-3 gap-6 mb-8 ${createScrollAnimationClasses('fadeInUp', statsVisible)}`}>
              <div className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Building size={24} className="text-white" />
                </div>
                <div className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">25+</div>
                <div className="text-sm text-gray-600 font-semibold">Cities Served</div>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Users size={24} className="text-white" />
                </div>
                <div className="text-2xl font-bold text-gray-900 group-hover:text-green-600 transition-colors">150+</div>
                <div className="text-sm text-gray-600 font-semibold">Expert Team</div>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Award size={24} className="text-white" />
                </div>
                <div className="text-2xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">15+</div>
                <div className="text-sm text-gray-600 font-semibold">Years Experience</div>
              </div>
            </div>

            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
              Learn More About Us
              <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          {/* Pioneering Smart Cities Images - Slides from Right */}
          <div ref={imagesRef} className={`relative ${createScrollAnimationClasses('fadeInRight', imagesVisible)}`}>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500">
                  <img 
                    src={process.env.PUBLIC_URL + "/Website_Work/08_Pioneering_Smart_Cities_Across_Oman/Seat_Belt_&_Mobile_usage_monitoring.png"}
                    alt="Seat Belt & Mobile Usage Monitoring"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                      <span className="text-sm font-semibold text-gray-800">Safety Technology</span>
                    </div>
                  </div>
                </div>
                <div className="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500">
                  <img 
                    src={process.env.PUBLIC_URL + "/Website_Work/08_Pioneering_Smart_Cities_Across_Oman/Security_Camera.jpg"}
                    alt="Security Camera Systems"
                    className="w-full h-35 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                      <span className="text-sm font-semibold text-gray-800">Security Solutions</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-6 mt-12">
                <div className="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500">
                  <img 
                    src={process.env.PUBLIC_URL + "/Website_Work/08_Pioneering_Smart_Cities_Across_Oman/Smart_city_lighting_pages-to-jpg-0005.jpg"}
                    alt="Smart City Lighting"
                    className="w-full h-35 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                      <span className="text-sm font-semibold text-gray-800">Smart Infrastructure</span>
                    </div>
                  </div>
                </div>
                <div className="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500">
                  <img 
                    src={process.env.PUBLIC_URL + "/Website_Work/08_Pioneering_Smart_Cities_Across_Oman/Speed_Lidar_system.jpg"}
                    alt="Speed LIDAR System"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                      <span className="text-sm font-semibold text-gray-800">Traffic Enforcement</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Enhanced Decorative Elements */}
            <div className="absolute -top-6 -left-6 w-32 h-35 bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-gradient-to-br from-green-400/20 to-blue-500/20 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-gradient-to-br from-purple-400/20 to-pink-500/20 rounded-full blur-3xl"></div>
          </div>
        </div>

        {/* Mission & Vision Cards */}
        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${createScrollAnimationClasses('fadeInUp', cardsVisible)}`}>
          <Card className="group relative overflow-hidden p-8 bg-gradient-to-br from-blue-50/80 to-indigo-50/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 backdrop-blur-sm">
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <Heart size={20} className="text-blue-500 animate-pulse" />
            </div>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-4 py-2 rounded-full mb-6">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-blue-700 text-sm font-semibold">Our Mission</span>
            </div>
            <h4 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 group-hover:text-blue-600 transition-colors">
              Connecting Cities Through Technology
            </h4>
            <p className="text-gray-600 leading-relaxed text-lg">
              To connect cities across Oman through leading-edge technology, creating smarter, 
              safer, and more sustainable urban environments that enhance the quality of life 
              for all residents and visitors.
            </p>
          </Card>

          <Card className="group relative overflow-hidden p-8 bg-gradient-to-br from-green-50/80 to-emerald-50/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 backdrop-blur-sm">
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <Star size={20} className="text-green-500 animate-pulse" />
            </div>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-100 to-emerald-100 px-4 py-2 rounded-full mb-6">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-green-700 text-sm font-semibold">Our Vision</span>
            </div>
            <h4 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 group-hover:text-green-600 transition-colors">
              Pioneering Smart Nation
            </h4>
            <p className="text-gray-600 leading-relaxed text-lg">
              To be the pioneering force in transforming Oman into a model smart nation, 
              where intelligent infrastructure seamlessly integrates with traditional values 
              to create the cities of tomorrow.
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};